import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './components/Header';
import { Footer } from './components/Footer';
import styles from './LegalPages.module.css';
import { Shield, Lock, AlertCircle } from 'lucide-react';

const PrivacyPolicy = () => {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, []);

    return (
        <div className={ styles.container }>
            <Header />

            <main className={ styles.main }>
                {/* Hero Section */ }
                <section className={ styles.hero }>
                    <div className={ styles.heroContent }>
                        <div className={ styles.iconBox }>
                            <Shield size={ 48 } />
                        </div>
                        <h1 className={ styles.title }>Privacy Policy</h1>
                        <p className={ styles.lastUpdated }>Last updated: 25 February 2026</p>
                    </div>
                </section>

                {/* Content Section */ }
                <section className={ styles.content }>
                    <div className={ styles.contentWrapper }>
                        {/* Introduction */ }
                        <div className={ styles.introBox }>
                            <AlertCircle size={ 24 } />
                            <div>
                                <p>
                                    Galile Technologies Private Limited ("Galile," "we," "our," or "us") respects your privacy and is committed to protecting personal data. This Privacy Policy explains how we collect, use, disclose, share, and protect information when you access or use the Galile website, products, services, APIs, analytics, and other related offerings (collectively, the "Services").
                                </p>
                                <p>
                                    By accessing or using the Services, you agree to the data practices described in this policy. This Privacy Policy is incorporated by reference into our <a href="/terms-of-service" onClick={ (e) => { e.preventDefault(); navigate('/terms-of-service'); } }>Terms of Service</a>.
                                </p>
                            </div>
                        </div>

                        {/* Table of Contents */ }
                        <div className={ styles.tocBox }>
                            <h3>Table of Contents</h3>
                            <div className={ styles.tocGrid }>
                                <a href="#about">1. About This Policy</a>
                                <a href="#information">2. Information We Collect</a>
                                <a href="#use">3. How We Use Information</a>
                                <a href="#legal-grounds">4. Legal Grounds for Processing</a>
                                <a href="#sharing">5. How We Share Data</a>
                                <a href="#processing">6. Data Processing on Behalf of Customers</a>
                                <a href="#retention">7. Data Retention</a>
                                <a href="#transfers">8. International Data Transfers</a>
                                <a href="#security">9. Security and Compliance</a>
                                <a href="#recording">10. Recording and Consents</a>
                                <a href="#children">11. Children's Privacy</a>
                                <a href="#rights">12. Your Rights</a>
                                <a href="#third-party">13. Third-Party Links</a>
                                <a href="#contact">14. How to Contact Us</a>
                            </div>
                        </div>

                        {/* Section 1 */ }
                        <div id="about" className={ styles.section }>
                            <h2>1. ABOUT THIS POLICY</h2>
                            <p>This policy applies to information collected:</p>
                            <ul>
                                <li>Through our website and marketing pages (e.g., www.galile.io)</li>
                                <li>When you register to use the Services</li>
                                <li>When you interact with our features such as meeting recording, transcription, analytics, and integrations</li>
                                <li>When you communicate with us for support</li>
                            </ul>
                            <p>
                                We may update this Privacy Policy from time to time. Material changes will be communicated via email or by posting the revised policy online.
                            </p>
                        </div>

                        {/* Section 2 */ }
                        <div id="information" className={ styles.section }>
                            <h2>2. INFORMATION WE COLLECT</h2>

                            <h3>2.1 Personal Data You Provide</h3>
                            <p>We collect information you directly provide, such as:</p>
                            <ul>
                                <li>Name, email address, phone number</li>
                                <li>Company name and role</li>
                                <li>Account credentials and profile settings</li>
                                <li>Billing and payment details</li>
                                <li>Support or sales inquiries</li>
                                <li>Data uploaded to the platform (e.g., CRM data, files, recordings)</li>
                            </ul>
                            <p>This type of data is used to provide, support, and improve the Services.</p>

                            <h3>2.2 Usage and Device Data</h3>
                            <p>When you use our Services, we automatically collect:</p>
                            <ul>
                                <li>IP address and approximate location</li>
                                <li>Browser and device type</li>
                                <li>Log-in and log-out times</li>
                                <li>Pages and features used</li>
                                <li>Interaction events and performance metrics</li>
                            </ul>
                            <p>This helps us operate the platform, measure engagement, and improve functionality.</p>

                            <h3>2.3 Service Content (Meeting Data)</h3>
                            <p>With your consent, the platform may process:</p>
                            <ul>
                                <li>Web conference recordings and phone calls</li>
                                <li>Transcripts</li>
                                <li>Notes, tags, and meeting analytics</li>
                            </ul>
                            <p>
                                This content may contain personal information such as names, email addresses, voice recordings, contact info, CRM fields, and other contextual details.
                            </p>

                            <h3>2.4 Cookies and Tracking Technologies</h3>
                            <p>We use cookies and similar technologies:</p>
                            <ul>
                                <li>To authenticate you</li>
                                <li>To analyze usage</li>
                                <li>To improve performance</li>
                                <li>For marketing and personalization</li>
                            </ul>
                            <p>Most browsers let you control cookie settings.</p>
                        </div>

                        {/* Section 3 */ }
                        <div id="use" className={ styles.section }>
                            <h2>3. HOW WE USE INFORMATION</h2>
                            <p>We process personal data for:</p>
                            <ul>
                                <li>Providing, maintaining, and improving the Services</li>
                                <li>Authenticating and securing accounts</li>
                                <li>Generating transcripts, analytics, and insights</li>
                                <li>Customer support and service communications</li>
                                <li>Billing, renewals, and order management</li>
                                <li>Marketing with your consent</li>
                                <li>Legal compliance and safety</li>
                            </ul>
                            <div className={ styles.highlightBox }>
                                <Lock size={ 20 } />
                                <p><strong>We do not sell personal data to third parties.</strong></p>
                            </div>
                        </div>

                        {/* Section 4 */ }
                        <div id="legal-grounds" className={ styles.section }>
                            <h2>4. LEGAL GROUNDS FOR PROCESSING</h2>
                            <p>Depending on your location and the data type, our legal bases include:</p>
                            <ul>
                                <li>Performance of a contract with you (to deliver the Services)</li>
                                <li>Legitimate business interests (e.g., security, service improvement)</li>
                                <li>Consent (e.g., meeting recording)</li>
                                <li>Compliance with law</li>
                            </ul>
                        </div>

                        {/* Section 5 */ }
                        <div id="sharing" className={ styles.section }>
                            <h2>5. HOW WE SHARE DATA</h2>
                            <p>We may share personal information with:</p>
                            <ul>
                                <li><strong>Service providers</strong> — e.g., hosting, analytics, support tools</li>
                                <li><strong>Payment processors</strong> — for billing</li>
                                <li><strong>Security and compliance partners</strong></li>
                                <li><strong>Affiliates and business partners</strong> (only as consented)</li>
                                <li><strong>Legal authorities</strong> — when required by law</li>
                            </ul>
                            <p>These partners are bound by confidentiality and security obligations.</p>
                            <div className={ styles.highlightBox }>
                                <Lock size={ 20 } />
                                <p><strong>We do not disclose personal data for advertising purposes without consent.</strong></p>
                            </div>
                        </div>

                        {/* Section 6 */ }
                        <div id="processing" className={ styles.section }>
                            <h2>6. DATA PROCESSING ON BEHALF OF CUSTOMERS</h2>
                            <p>When you are a customer of Galile and upload or generate content through the Services, we act as a data processor on your behalf:</p>
                            <ul>
                                <li>We store, host, and process the data to deliver the Services</li>
                                <li>We do not use customer content for independent advertising or profiling</li>
                                <li>We help enforce data subject rights where required</li>
                            </ul>
                            <p>
                                Customers are responsible for obtaining required consent for meetings and recordings and complying with local laws.
                            </p>
                        </div>

                        {/* Section 7 */ }
                        <div id="retention" className={ styles.section }>
                            <h2>7. DATA RETENTION</h2>
                            <p>We retain personal data:</p>
                            <ul>
                                <li>As long as needed to provide Services</li>
                                <li>To comply with legal obligations</li>
                                <li>To resolve disputes or enforce agreements</li>
                            </ul>
                            <p>You may request deletion in accordance with applicable law.</p>
                        </div>

                        {/* Section 8 */ }
                        <div id="transfers" className={ styles.section }>
                            <h2>8. INTERNATIONAL DATA TRANSFERS</h2>
                            <p>
                                Galile operates globally, and data may be stored or processed outside your home jurisdiction. This may include countries without equivalent privacy protections. We apply safeguards like standard contractual clauses and encryption to secure international transfers.
                            </p>
                        </div>

                        {/* Section 9 */ }
                        <div id="security" className={ styles.section }>
                            <h2>9. SECURITY AND COMPLIANCE</h2>
                            <p>We implement reasonable administrative, technical, and physical protections, including:</p>
                            <ul>
                                <li>Encryption in transit and at rest</li>
                                <li>Access controls and monitoring</li>
                                <li>Secure cloud infrastructure</li>
                            </ul>
                            <p>
                                However, no system is entirely secure. We constantly improve defenses to protect data integrity and confidentiality.
                            </p>
                        </div>

                        {/* Section 10 */ }
                        <div id="recording" className={ styles.section }>
                            <h2>10. RECORDING AND CONSENTS</h2>
                            <div className={ styles.warningBox }>
                                <p>
                                    Some Services include voice or video recording and transcription. You are responsible for informing meeting participants and ensuring required legal consent is obtained before recording in jurisdictions that require one-party or all-party consent.
                                </p>
                                <p>We process recordings only for the purposes of providing the Service.</p>
                            </div>
                        </div>

                        {/* Section 11 */ }
                        <div id="children" className={ styles.section }>
                            <h2>11. CHILDREN'S PRIVACY</h2>
                            <p>
                                The Services are not intended for individuals under 18. We do not knowingly collect personal information from minors.
                            </p>
                        </div>

                        {/* Section 12 */ }
                        <div id="rights" className={ styles.section }>
                            <h2>12. YOUR RIGHTS</h2>
                            <p>Where applicable, you may have rights including:</p>
                            <ul>
                                <li>Access your personal data</li>
                                <li>Correct inaccuracies</li>
                                <li>Request deletion or restriction</li>
                                <li>Object to processing</li>
                                <li>Data portability</li>
                                <li>Withdraw consent</li>
                            </ul>
                            <p>
                                To exercise rights, contact us at <a href="mailto:sagar@galile.io">sagar@galile.io</a>.
                            </p>
                        </div>

                        {/* Section 13 */ }
                        <div id="third-party" className={ styles.section }>
                            <h2>13. THIRD-PARTY LINKS</h2>
                            <p>
                                Our Services may link to external sites with their own privacy policies. We are not responsible for third-party practices.
                            </p>
                        </div>

                        {/* Section 14 */ }
                        <div id="contact" className={ styles.section }>
                            <h2>14. HOW TO CONTACT US</h2>
                            <p>
                                Please contact us by emailing to <a href="mailto:sagar@galile.io">sagar@galile.io</a> with any questions regarding this Privacy Policy.
                            </p>
                        </div>

                        {/* Bottom CTA */ }
                        <div className={ styles.bottomCta }>
                            <Shield size={ 32 } />
                            <h3>Your privacy matters to us</h3>
                            <p>Have questions about how we handle your data? We're here to help.</p>
                            <button onClick={ () => navigate('/contact') } className={ styles.ctaButton }>
                                Contact Us
                            </button>
                        </div>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
};

export default PrivacyPolicy;
